import React, { useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SortableTable from '../../../../components/common/SortableTable/SortableTable.jsx';
import Pagination from '../../../../components/common/Pagination/Pagination.jsx';
import ConfirmationModal from '../../../../components/ui/ConfirmationModal/ConfirmationModal.jsx';
import { useConfirmation } from '../../../../hooks/useConfirmation';
import { useDateTime } from '../../../../hooks/useDateTime';

// Separate search input component to isolate re-renders
const SearchInput = React.memo(({ value, onChange, placeholder }) => (
    <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="search-input"
    />
));

// Separate action button to prevent re-renders
const ActionButton = React.memo(({ onClick, text, disabled }) => (
    <button 
        onClick={onClick}
        className="btn btn-primary"
        disabled={disabled}
    >
        {text}
    </button>
));

// Loading overlay component
const LoadingOverlay = React.memo(({ loading }) => 
    loading ? (
        <div className="table-loading-overlay">
            <div className="spinner"></div>
            <span>Loading...</span>
        </div>
    ) : null
);

const CategoryTable = React.memo(({
    hookData,
    onAddCategory,
    onEditCategory
}) => {
    const { t } = useTranslation(['inventory', 'common']);
    const { isOpen, confirmationConfig, confirm, closeConfirmation } = useConfirmation();
    const { formatDate } = useDateTime();
    const [deletingItemId, setDeletingItemId] = useState(null);

    // Table columns configuration with proper memoization
    const columns = useMemo(() => [
        { 
            key: 'name', 
            label: t('inventory:name'), 
            sortable: true,
            render: (category) => <strong>{category.name}</strong>
        },
        { 
            key: 'description', 
            label: t('inventory:description'), 
            sortable: true,
            render: (category) => category.description || <em className="text-muted">{t('common:noDescription')}</em>
        },
        {
            key: 'product_count',
            label: t('inventory:productCount'),
            sortable: true,
            render: (category) => (
                <span className="badge">
                    {category.product_count || 0} {t('inventory:products').toLowerCase()}
                </span>
            )
        },
        {
            key: 'created_at',
            label: t('inventory:dateCreated'),
            sortable: true,
            render: (category) => formatDate(category.created_at)
        }
    ], [t, formatDate]);

    // Handle delete with confirmation
    const handleDeleteClick = useCallback(async (category) => {
        const confirmed = await confirm({
            title: t('common:confirmation'),
            message: t('inventory:confirmDeleteCategory', { name: category.name }),
            confirmText: t('common:buttons.delete'),
            confirmButtonClass: 'btn-danger'
        });

        if (confirmed) {
            setDeletingItemId(category.id);
            await hookData.deleteCategory(category.id);
            setDeletingItemId(null);
        }
    }, [confirm, t, hookData]);

    // Memoized pagination props
    const paginationProps = useMemo(() => ({
        pagination: hookData.pagination,
        onPageChange: hookData.onPageChange,
        onLimitChange: hookData.onLimitChange,
        limitOptions: hookData.limitOptions
    }), [
        hookData.pagination,
        hookData.onPageChange,
        hookData.onLimitChange,
        hookData.limitOptions
    ]);

    return (
        <div className="inventory-content">
            {/* Search and Action Bar - isolated components */}
            <div className="action-bar">
                <SearchInput
                    value={hookData.search}
                    onChange={hookData.onSearchChange}
                    placeholder={t('inventory:searchCategories')}
                />
                <ActionButton
                    onClick={onAddCategory}
                    text={`${t('common:add')} ${t('inventory:category')}`}
                    disabled={hookData.actionLoading}
                />
            </div>

            {/* Table with loading overlay - isolated re-renders */}
            <div style={{ position: 'relative' }}>
                <SortableTable
                    columns={columns}
                    data={hookData.categories}
                    onEdit={onEditCategory}
                    onDelete={handleDeleteClick}
                    sortField={hookData.sort}
                    sortOrder={hookData.order}
                    onSort={hookData.onSortChange}
                    emptyMessage={hookData.search ? t('inventory:noCategoriesSearch') : t('inventory:noCategoriesFound')}
                />
                <LoadingOverlay loading={hookData.loading} />
            </div>

            {/* Pagination Controls - memoized */}
            {hookData.pagination && (
                <Pagination {...paginationProps} />
            )}

            <ConfirmationModal
                isOpen={isOpen}
                onClose={closeConfirmation}
                onConfirm={confirmationConfig.onConfirm}
                title={confirmationConfig.title}
                message={confirmationConfig.message}
                confirmText={confirmationConfig.confirmText}
                cancelText={confirmationConfig.cancelText}
                confirmButtonClass={confirmationConfig.confirmButtonClass}
                loading={deletingItemId !== null}
            />
        </div>
    );
});

// Display name for React DevTools
CategoryTable.displayName = 'CategoryTable';

export default CategoryTable;